import React, { createContext, useContext, useState, useCallback } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { readTabPaths, saveTabPaths } from './tabSession';

const TabContext = createContext();

// Map route paths to human-readable labels
const ROUTE_LABELS = {
  '/Home': 'Home',
  '/RoleIndex': 'Roles',
  '/RoleDetail': 'Role Detail',
  '/UserPermissionIndex': 'User Permissions',
  '/UserIndex': 'Users',
  '/UserCreate': 'Create User',
  '/UserEdit': 'Edit User',
  '/UserDetail': 'User Detail',
  '/PasswordChange': 'Password',
  '/ProductCategoryIndex': 'Category',
  '/ProductItemIndex': 'Items',
  '/ProductBrandIndex': 'Brand',
  '/ProductRelationIndex': 'Product Relations',
  '/CurrencyTypeIndex': 'Currency',
  '/TermsAndConditionsIndex': 'Terms & Conditions',
  '/BankIndex': 'Bank',
  '/MasterDashboard': 'Master',
  '/ClientIndex': 'Customers',
  '/VendorIndex': 'Suppliers',
  '/AccountHeadDashboard': 'Account Head',
  '/AccountHeadIndex': 'Account Head',
  '/AssetIndex': 'Asset',
  '/CapitalIndex': 'Capital',
  '/OtherOptionIndex': 'Others',
  '/BalanceSheetHeadIndex': 'Balance Sheet',
  '/PurchaseOrderIndex': 'Purchase Order',
  '/PurchaseIndex': 'Purchase',
  '/PurchaseReturnAgainstIndex': 'Purchase Return',
  '/QuotationIndex': 'Quotation',
  '/SaleOrderIndex': 'Sale Order',
  '/SaleIndex': 'Sale',
  '/SaleReturnAgainstIndex': 'Sale Return',
  '/StockTransferInIndex': 'Stock In',
  '/StockTransferOutIndex': 'Stock Out',
  '/PaymentAndReceiptIndex': 'Payment & Receipt',
  '/JournalEntryIndex': 'Journal Entry',
  '/PettyCashIndex': 'Petty Cash',
  '/TrialBalanceReport': 'Trial Balance',
  '/LedgerReport': 'Ledger Report',
  '/VendorDueListReport': 'Vendor Due',
  '/CustomerDueListReport': 'Customer Due',
  '/OutstandingReport': 'Outstanding',
  '/VendorAgingReport': 'Vendor Aging',
  '/CustomerAgingReport': 'Customer Aging',
  '/PurchasePartyandItemWiseReport': 'Purchase Report',
  '/SalePartyandItemWiseReport': 'Sale Report',
  '/CashBook': 'Cash Book',
  '/StockReport': 'Stock Report',
  '/DetailedInvoiceReport': 'Invoice Report',
  '/IncomeExpenseSummaryReport': 'Income & Expense',
  '/PettyCashReport': 'Petty Cash Report',
  '/CompanyEdit': 'Company',
  '/TrialBalanceAdvancedReport': 'Trial Balance Advanced',
  '/Ledger': 'Ledger Report',
  '/VendorDueList': 'Vendor Due',
  '/CustomerDueList': 'Customer Due',
  '/Outstanding': 'Outstanding',
  '/VendorAging': 'Vendor Aging',
  '/CustomerAging': 'Customer Aging',
  '/IncomeExpenseSummary': 'Income & Expense',
  '/StaffDashboard': 'Staff',
  '/DepartmentIndex': 'Department',
  '/DesignationIndex': 'Designation',
};

export const getLabelForPath = (path) => {
  if (!path || path === '/' || path === '/Login') return 'Home';
  // Exact match first
  if (ROUTE_LABELS[path]) return ROUTE_LABELS[path];
  // Case-insensitive match
  const lowerPath = path.toLowerCase();
  const matchedKey = Object.keys(ROUTE_LABELS).find(k => k.toLowerCase() === lowerPath);
  if (matchedKey) return ROUTE_LABELS[matchedKey];
  // Prefix match for dynamic routes like /RoleDetail/123
  const segments = path.split('/').filter(Boolean);
  if (segments.length > 0) {
    const base = '/' + segments[0];
    const matchedBaseKey = Object.keys(ROUTE_LABELS).find(k => k.toLowerCase() === base.toLowerCase());
    if (matchedBaseKey) return ROUTE_LABELS[matchedBaseKey];
    return segments[0].replace(/([A-Z])/g, ' $1').trim();
  }
  return 'Home';
};

export const TabProvider = ({ children }) => {
  const history = useHistory();
  const location = useLocation();

  const [tabs, setTabs] = useState(() => {
    const currentPath = location.pathname;
    const paths = ['/Home', ...readTabPaths()];
    if (currentPath && currentPath !== '/' && currentPath !== '/Login') {
      paths.push(currentPath);
    }
    const seen = new Set();
    return paths.filter(path => {
      const key = path.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    }).map(path => ({
      path: path.toLowerCase() === currentPath.toLowerCase() ? currentPath : path,
      label: getLabelForPath(path),
    }));
  });

  React.useEffect(() => {
    saveTabPaths(tabs);
  }, [tabs]);

  const [activeTab, setActiveTab] = useState(() => {
    const currentPath = location.pathname;
    if (currentPath && currentPath !== '/' && currentPath !== '/Login') {
      return currentPath;
    }
    return '/Home';
  });

  const openTab = useCallback((path) => {
    if (!path || path === '/' || path === '/Login') {
      setActiveTab('/Home');
      history.push('/Home');
      return;
    }
    const label = getLabelForPath(path);
    setTabs(prev => {
      const exists = prev.find(t => t.path.toLowerCase() === path.toLowerCase());
      if (exists) return prev;
      return [...prev, { path, label }];
    });
    setActiveTab(path);
    history.push(path);
  }, [history]);

  const closeTab = useCallback((path, e) => {
    e && e.stopPropagation();
    if (path === '/Home') return; // Cannot close Home tab
    setTabs(prev => {
      const idx = prev.findIndex(t => t.path === path);
      if (idx === -1) return prev;
      const next = prev.filter(t => t.path !== path);
      // If closing active tab, switch to adjacent tab
      if (path === activeTab) {
        const fallback = next[Math.max(0, idx - 1)] || next[0] || { path: '/Home' };
        const newActive = fallback.path;
        setActiveTab(newActive);
        history.push(newActive);
      }
      return next;
    });
  }, [activeTab, history]);

  const switchTab = useCallback((path) => {
    const target = (!path || path === '/' || path === '/Login') ? '/Home' : path;
    setActiveTab(target);
    history.push(target);
  }, [history]);

  // Sync activeTab when browser back/forward is used
  React.useEffect(() => {
    const currentPath = location.pathname;
    if (!currentPath || currentPath === '/' || currentPath === '/Login') {
      setActiveTab('/Home');
      if (currentPath !== '/Home') {
        history.replace('/Home');
      }
      setTabs(prev => {
        const cleaned = prev.filter(t => t.path && t.path !== '/' && t.path !== '/Login');
        return cleaned.length === 0 ? [{ path: '/Home', label: 'Home' }] : (cleaned.length === prev.length ? prev : cleaned);
      });
      return;
    }

    const label = getLabelForPath(currentPath);
    setTabs(prev => {
      const cleaned = prev.filter(t => t.path && t.path !== '/' && t.path !== '/Login');
      const exists = cleaned.find(t => t.path.toLowerCase() === currentPath.toLowerCase());
      if (exists) return cleaned.length === prev.length ? prev : cleaned;
      return [...cleaned, { path: currentPath, label }];
    });
    setActiveTab(currentPath);
  }, [location.pathname, history]);

  return (
    <TabContext.Provider value={{ tabs, activeTab, openTab, closeTab, switchTab }}>
      {children}
    </TabContext.Provider>
  );
};

export const useTabs = () => useContext(TabContext);
